/* eslint-disable no-nested-ternary */
import { Fragment, useEffect, useState } from 'react';
import { BsTicketPerforated } from 'react-icons/bs';
import MenuDropdown from './atoms/MenuDropdown';
import CustomModal from './atoms/CustomModal';
import { useDeleteTicketMutation } from '../store';
import Button from './atoms/Button';
import TicketDTO, { TicketPublicSaleStatus } from '../models/ticket';
import CreateTicket from './CreateTicket';
import FilterTable, { BaseData } from './Table/Table';
import { formatCurrency } from '../utils/shared';
import EmptyTable from './Table/EmptyTable';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';

interface Props {
  tickets: TicketDTO[];
  eventId: string;
  businessId: string;
  showtimeId: string | null;
  button?: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    variant: 'primary' | 'secondary' | 'tertiary';
  };
}

interface TableTicket extends BaseData {
  id: string;
  name: string;
  amount_taken: string;
  price: JSX.Element;
  status: JSX.Element;
}

const TicketsTable: React.FC<Props> = ({
  tickets,
  eventId,
  businessId,
  showtimeId,
  button,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'edit' | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDTO | null>(null);
  const [deleteTicket] = useDeleteTicketMutation();
  const handleConfirmDeleteTicket = (ticket: TicketDTO) => {
    deleteTicket({
      businessId,
      eventId,
      ticketId: ticket.id,
    });
    setShowModal(false);
    setModalType(null);
    setSelectedTicket(null);
  };

  const { event } = useGetSelectedEvent();

  const [showtimeTickets, setShowtimeTickets] = useState<TicketDTO[]>([]);

  useEffect(() => {
    if (showtimeId) {
      setShowtimeTickets(tickets.filter((ticket) => ticket.showtime_id === showtimeId));
    } else {
      setShowtimeTickets(tickets);
    }
  }, [showtimeId, tickets]);

  const handleClose = () => {
    setShowModal(false);
    setModalType(null);
    setSelectedTicket(null);
  };

  const handleDeleteTicket = (ticket: TicketDTO) => {
    setSelectedTicket(ticket);
    setModalType('delete');
    setShowModal(true);
  };

  const handleEditTicket = (ticket: TicketDTO) => {
    setSelectedTicket(ticket);
    setModalType('edit');
    setShowModal(true);
  };

  const customButton = button && (
    <Button onClick={button.onClick} variant={button.variant}>
      <div className="flex items-center justify-center gap-2 p-2">
        {button.icon}
        <p className="hidden min-[1150px]:block whitespace-nowrap">
          {button.label}
        </p>
      </div>
    </Button>
  );

  const dropDownOptions = [
    {
      label: 'Editar',
      onClick: handleEditTicket,
    },
    {
      label: 'Eliminar',
      onClick: handleDeleteTicket,
    },
  ];

  const config = [
    {
      label: 'Nombre',
      id: 'name',
    },
    {
      label: 'Estado',
      id: 'status',
    },
    {
      label: 'Cantidad',
      id: 'amount_taken',
    },
    {
      label: 'Precio',
      id: 'price',
    },
  ];

  const rows: TableTicket[] = showtimeTickets.map((ticket) => ({
    id: ticket.id,
    name: ticket.name,
    amount_taken: `${ticket.amount_taken} / ${ticket.total_amount}`,
    price: <div className='whitespace-nowrap'>{formatCurrency(ticket.price)}</div>,
    status: ticket.active ? (
      <div className='flex flex-row gap-2'>
        <div className='tag-primary'>
          Visible
        </div>
        {ticket.few_tickets_left && !(ticket.public_sale_status === TicketPublicSaleStatus.SoldOut) && (
          <div className='tag-secondary'>
            Quedan pocas
          </div>
        )}
        {ticket.public_sale_status === TicketPublicSaleStatus.SoldOut && (
          <div className='tag-secondary'>
            Entradas agotadas
          </div>
        )}
      </div>
    ) : (
      <div className='tag-warning'>
        No visible
      </div>
    ),
  }));

  const renderActions = (row: TableTicket) => (
    <MenuDropdown
      data={row}
      options={dropDownOptions.map((option) => ({
        ...option,
        onClick: () => {
          const ticket = showtimeTickets.find((t) => t.id === row.id);
          if (ticket) option.onClick(ticket);
        },
      }))}
    />
  );

  return (
    <div>
      {showtimeTickets.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={config}
          actions={renderActions}
          pagination={{ enabled: true, pageSize: 10 }}
          searchEnabled={true}
          customButtons={customButton ? [customButton] : []}
          tableId="tickets-table"
        />
      ) : (
        event?.has_showtimes ? <EmptyTable
          title="Para poder crear entradas, primero debes crear funciones."
          info="Desde el botón superior derecho de funciones podrás crear nuevas."
          icon={BsTicketPerforated}
          iconColor="text-secondary-600"
          button={button ? [
            {
              label: button?.label,
              reactIcon: button?.icon,
              onClick: button?.onClick,
              variant: button?.variant,
            },
          ] : undefined}
        />
          : <EmptyTable
            title="Aún no hay entradas en este evento"
            info="Aquí aparecerán las entradas que crees en este evento"
            icon={BsTicketPerforated}
            iconColor="text-secondary-600"
            button={button ? [
              {
                label: button?.label,
                reactIcon: button?.icon,
                onClick: button?.onClick,
                variant: button?.variant,
              },
            ] : undefined}
          />)}
      <CustomModal
        onRequestClose={handleClose}
        isOpen={showModal}
        hideCancelButton={true}
      >
        <div className='flex flex-col gap-4 w-[55vw] max-w-lg'>
          {modalType === 'delete' && selectedTicket && (
            <div className="flex flex-col gap-4">
              {selectedTicket.amount_taken === 0 ? (
                <div className='flex flex-col gap-3'>
                  <p className="text-xl font-medium">¿Deseas eliminar esta entrada?</p>
                  <p className="text-text-secondary">
                    Una vez eliminada desaparecerá del listado.
                  </p>
                  <div className="flex flex-row gap-2">
                    <Button variant="secondary" onClick={handleClose}>
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => handleConfirmDeleteTicket(selectedTicket)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='flex flex-col gap-3'>
                  <p className="text-xl font-medium">Esta entrada no se puede eliminar</p>
                  <p className="text-text-secondary">
                    Esta entrada no se puede eliminar porque ya tiene ventas.
                  </p>
                  <Button variant="primary" onClick={handleClose}>
                    Aceptar
                  </Button>
                </div>
              )}
            </div>
          )}
          {modalType === 'edit' && selectedTicket && (
            <Fragment>
              <div className="flex flex-col justify-center items-center">
                <h1 className="text-2xl font-medium">Editar entrada</h1>
              </div>
              <CreateTicket
                ticket={selectedTicket}
                onSuccess={handleClose}
                onCancel={handleClose}
                showtimeId={showtimeId}
              />
            </Fragment>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default TicketsTable;
